import { useCallback, useEffect, useRef, useState } from 'react';

import { monitorApi } from '../api/monitor';
import type {
  LineNumber,
  LineStatusFilter,
  LineSyncResult,
  NumbersPage,
  OperatorOption,
} from '../api/monitor';

/** Tempo de digitação parada antes de buscar — evita um request por tecla. */
const TYPING_MS = 350;

export interface NumbersController {
  page: NumbersPage | null;
  operators: OperatorOption[];
  /** Nomes de master da base de linhas — as opções do filtro. */
  masters: string[];
  /** `null` = todas as operadoras. */
  operatorCode: string | null;
  /** Nomes de master filtrados; vazio = todos. */
  selectedMasters: string[];
  /** O que está no campo de busca, sem esperar o debounce. */
  query: string;
  /** Situação filtrada; a tela abre nas ativas. */
  status: LineStatusFilter;
  /** Segmentos Claro cujo consumo é exibido; vazio = consumo total. */
  segments: string[];
  loading: boolean;
  error: string | null;
  /** Chave (`lineKey`) da linha sendo sincronizada agora; nula quando nenhuma está. */
  syncing: string | null;
  setOperator: (code: string | null) => void;
  setSelectedMasters: (names: string[]) => void;
  setStatus: (status: LineStatusFilter) => void;
  setSegments: (segments: string[]) => void;
  setQuery: (value: string) => void;
  goToPage: (page: number) => void;
  reload: () => void;
  /** Sincroniza uma linha e devolve o resultado para a tela avisar o usuário. */
  syncLine: (number: LineNumber) => Promise<LineSyncResult>;
}

/** Uma linha é única por operadora + número; é a chave da sincronização na tela. */
export function lineKey(number: Pick<LineNumber, 'line' | 'operator'>): string {
  return `${number.operator.code}:${number.line}`;
}

export function useNumbers(): NumbersController {
  const [page, setPage] = useState<NumbersPage | null>(null);
  const [operators, setOperators] = useState<OperatorOption[]>([]);
  const [masters, setMasters] = useState<string[]>([]);
  const [operatorCode, setOperatorCode] = useState<string | null>(null);
  const [selectedMasters, setSelectedMastersValue] = useState<string[]>([]);
  const [query, setQueryValue] = useState('');
  // Ativas por padrão: é o que se procura quase sempre, e é o que o card de
  // linhas do dashboard conta — a tela abre batendo com ele.
  const [status, setStatusValue] = useState<LineStatusFilter>('active');
  // Segmentos Claro; vazio mostra o consumo total (comportamento anterior).
  const [segments, setSegmentsValue] = useState<string[]>([]);
  // O que já passou pelo debounce; é ele que entra no request.
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Respostas voltam fora de ordem quando o usuário troca o filtro no meio do
  // caminho; só a última pedida pode escrever na tela.
  const latestRequest = useRef(0);

  useEffect(() => {
    monitorApi
      .operators()
      .then((response) => setOperators(response.operators))
      .catch(() => setOperators([]));

    // Uma vez por abertura da tela; o servidor ainda cacheia a consulta, que
    // varre a base de linhas — masters mudam pouco.
    monitorApi
      .lineMasters()
      .then((response) => setMasters(response.masters))
      .catch(() => setMasters([]));
  }, []);

  useEffect(() => {
    if (query === search) return;

    const timer = window.setTimeout(() => {
      setSearch(query);
      setPageNumber(1);
    }, TYPING_MS);

    return () => window.clearTimeout(timer);
  }, [query, search]);

  useEffect(() => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    setLoading(true);

    monitorApi
      .numbers({
        operatorCode,
        line: search,
        masters: selectedMasters,
        status,
        groups: segments,
        page: pageNumber,
      })
      .then((response) => {
        if (latestRequest.current !== requestId) return;
        setPage(response);
        setError(null);
      })
      .catch((cause: Error) => {
        if (latestRequest.current !== requestId) return;
        setError(cause.message);
      })
      .finally(() => {
        if (latestRequest.current !== requestId) return;
        setLoading(false);
      });
  }, [operatorCode, selectedMasters, search, status, segments, pageNumber, reloadToken]);

  const setOperator = useCallback((code: string | null) => {
    setOperatorCode(code);
    // O segmento é só da Claro; ao sair dela, volta ao consumo total.
    if (code !== 'claro') setSegmentsValue([]);
    setPageNumber(1);
  }, []);

  const setSelectedMasters = useCallback((names: string[]) => {
    setSelectedMastersValue(names);
    setPageNumber(1);
  }, []);

  const setStatus = useCallback((next: LineStatusFilter) => {
    setStatusValue(next);
    setPageNumber(1);
  }, []);

  const setSegments = useCallback((next: string[]) => {
    setSegmentsValue(next);
    setPageNumber(1);
  }, []);

  const goToPage = useCallback((next: number) => setPageNumber(Math.max(1, next)), []);

  const reload = useCallback(() => setReloadToken((token) => token + 1), []);

  /**
   * A resposta traz a linha inteira já atualizada, então trocamos só ela na
   * página que está na tela. Recarregar a listagem custaria uma volta ao banco
   * e, pior, moveria a linha de lugar se alguém tivesse mudado o filtro no
   * meio do caminho.
   */
  const syncLine = useCallback(async (number: LineNumber) => {
    const key = lineKey(number);

    setSyncing(key);

    try {
      // Pela operadora + número, e não pelo id do cadastro: vale para toda
      // linha da tela, inclusive a que só existe na leitura diária.
      const { results } = await monitorApi.syncNumberByLine(number.line, number.operator.code);
      const result = results[0];

      if (!result) throw new Error('A linha não pôde ser sincronizada.');

      setPage((current) =>
        current
          ? {
              ...current,
              numbers: current.numbers.map((entry) =>
                lineKey(entry) === key ? result.number : entry,
              ),
            }
          : current,
      );

      return result;
    } finally {
      setSyncing(null);
    }
  }, []);

  return {
    page,
    operators,
    masters,
    operatorCode,
    selectedMasters,
    query,
    status,
    segments,
    loading,
    error,
    syncing,
    setOperator,
    setSelectedMasters,
    setStatus,
    setSegments,
    setQuery: setQueryValue,
    goToPage,
    reload,
    syncLine,
  };
}
